import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { obfuscatedEnrollmentQueryKey } from '../libs/ReactQueryHooks'
import { getGrooverText, getGrooverTextStyle } from '../libs/GrooverTextRepository'
import Box from './Box'
import { styled } from '@mui/material'
import { ContinueButton } from './StyledComponents'
import { getCourselyPrivacyPolicyUrl, getCourselyUserConditionsUrl } from '../enums/HardCodedValues'
import { useClient } from '../hooks/useClient'
import Spinner from './Spinner'
import GrooverText from './GrooverText'
import {
  Activity,
  EnrollmentPublic,
  EnrollmentRequestV3,
  EnrollmentStatus,
  PaymentMethod
} from '../../../pure-js/types/GrooverTypes'
import { State } from '../hooks/useAppState'
import { saveUserRegistrationV3, saveUserRegistrationV4 } from '../libs/CloudFunctionsApiHandler'
import { useHardCoupleSplitRegistrationEnabled } from '../hooks/useHardCoupleSplitRegistrationEnabled'
import { StorageKey } from '../hooks/useLocalStorageItemHelper'
import iconCompleted from '/public/iconCompleted.svg'
import iconArrow from '/public/iconArrow.svg'
import checkIcon from '/public/checkIcon.svg'
import { EIGHT_PX, FOUR_PX, SIXTEEN_PX } from '../enums/Spacings'
import { useEnrollmentPublic } from '../libs/DBApiHandler'
import CourseRegistrationApprovalWaiting from './CourseRegistrationApprovalWaiting'
import { getRegisteredPartnersFromState } from '../../../pure-js/libs/calculateLogic/CalculateDiscountHelpers'
import { PriceOverride } from '../../../pure-js/types/DiscountTypes'
import { getLanguageCode } from '../hooks/useDefaultLanguage'
import { InfoOutlined, Error as ErrorIcon } from '@mui/icons-material'
import { createUniqueIdLong } from '../../../pure-js/libs/Common'
import { resolveMainUserRoleForSubmit } from '../helpers/RegistrationRoleHelper'

const SUPPORT_EMAIL = 'support@coursely.se'

const SupportEmailLink = styled('a')`
  cursor: pointer;
  color: #0066cc;
  text-decoration: underline;

  &:hover {
    color: #0052a3;
    text-decoration: none;
  }

  &:active {
    color: #003d7a;
  }
`

type Props = {
  state: State
  activity: Activity
  onNextStep: () => void
  onSetIsRegistrationSubmitted: () => void
  onSetEnrollmentId: (enrollmentId: string) => void
}

const CourseRegistrationStepThreeOpenedContent = ({
  state,
  activity,
  onNextStep,
  onSetIsRegistrationSubmitted,
  onSetEnrollmentId
}: Props) => {
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const [isSlowSave, setIsSlowSave] = useState(false)
  const slowSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false)
  const [enrollmentStatus, setEnrollmentStatus] = useState<EnrollmentPublic['status']>()
  const [roleValidationError, setRoleValidationError] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [emailCopied, setEmailCopied] = useState(false)

  const client = useClient(activity)
  const language = state.user?.language || getLanguageCode()
  const privacyPolicyUrl = client?.customPrivacyUrl || getCourselyPrivacyPolicyUrl(language)
  const userConditionsUrl = client?.customConditionsUrl || getCourselyUserConditionsUrl(language)

  const enrollmentPublic = useEnrollmentPublic(
    !state.enrollmentId ? state.obfuscatedEnrollment?.id : state.enrollmentId
  )

  const useSplitRegistration = useHardCoupleSplitRegistrationEnabled(activity.clientId)

  const hasPartner = state.partnerName && state.partnerEmail && state.partnerPhoneNumber && state.partnerRole
  const hasMainUser = !!state.name && !!state.email && !!state.phoneNumber
  const isRegistrationCompleted = state.isRegistrationCompleted || state.isRegistrationSubmitted
  const isCheckboxDisabled = !isCheckboxChecked || !hasMainUser || state.hasFormErrors

  useEffect(() => {
    if (isLoading) {
      slowSaveTimerRef.current = setTimeout(() => setIsSlowSave(true), 5000)
    } else {
      if (slowSaveTimerRef.current) {
        clearTimeout(slowSaveTimerRef.current)
        slowSaveTimerRef.current = null
      }
      setIsSlowSave(false)
    }
    return () => {
      if (slowSaveTimerRef.current) {
        clearTimeout(slowSaveTimerRef.current)
      }
    }
  }, [isLoading])

  const handleCheckboxChange = useCallback(() => {
    if (!isRegistrationCompleted) {
      setIsCheckboxChecked((prev) => !prev)
    }
  }, [isRegistrationCompleted])

  const handleContinue = useCallback(() => {
    if (state.isRegistrationSubmitted) return onNextStep()
    if (isCheckboxDisabled || !activity.clientId) return

    setRoleValidationError(false)
    setSubmitError(false)
    setEmailCopied(false)

    const mainUserRole = resolveMainUserRoleForSubmit(state, activity)
    if (!mainUserRole) {
      setRoleValidationError(true)
      return
    }

    setIsLoading(true)

    const priceOverride: PriceOverride[] = Object.keys(state.earlyBirdBooking).map((activityId) => ({
      activityId,
      price: state.earlyBirdBooking[activityId].price,
      reference: state.earlyBirdBooking[activityId].bookingId,
      type: 'earlyBird'
    }))

    // Build per-activity children from state.childrenInput
    const childrenForActivities = state.selectedActivities
      .filter((a) => a.allowChildRegistration && state.childrenInput[a.id]?.name?.trim())
      .map((a) => {
        const input = state.childrenInput[a.id]
        const parsedYear = parseInt(input.birthYear, 10)
        return {
          activityId: a.id,
          child: {
            id: createUniqueIdLong(),
            name: input.name.trim(),
            birthYear: !isNaN(parsedYear) ? parsedYear : undefined,
            createdAt: new Date().toISOString()
          }
        }
      })

    const mainUserRegistration: EnrollmentRequestV3 = {
      clientId: activity.clientId,
      mainUser: {
        name: state.name,
        email: state.email.trim(),
        phone: state.phoneNumber,
        type: mainUserRole,
        ssn: state.mainUserSsn?.trim(),
        address: state.mainUserAddress?.trim() || undefined,
        ...(childrenForActivities.length > 0 && {
          children: childrenForActivities.map((c) => c.child),
          isParent: true
        })
      },
      activityIds: state.selectedActivities.map((activity) => activity.id),
      activityQuantities:
        activity.eventType === 'SingleEvent' ? { [activity.id]: state.singleEventQuantity || 1 } : undefined,
      paymentInput: [{ user: 'mainUser', method: PaymentMethod.BankTransfer }],
      discountUserInput: {
        coursesAmount: state.selectedActivities.length,
        participantsAmount: 1,
        mainUser: {
          isStudent: state.isMainUserStudent,
          isSibling: state.isMainUserSibling,
          isSenior: state.isMainUserSenior,
          userActivePeriods: state.mainUserActivePeriods
        },
        priceOverride,
        discountCode: state.discountCode
      },
      comment: state.enrollmentComment,
      env: process.env.NODE_ENV || 'local',
      ...(childrenForActivities.length > 0 && {
        childrenByActivity: Object.fromEntries(childrenForActivities.map((c) => [c.activityId, [c.child.id]]))
      })
    }

    const registrationData: EnrollmentRequestV3 = hasPartner
      ? {
          ...mainUserRegistration,
          partnerUser: {
            name: state.partnerName!,
            email: state.partnerEmail?.trim() ?? '', // safe trim so TS is not complaining; email is always set there
            phone: state.partnerPhoneNumber!,
            type: state.partnerRole!,
            ssn: state.partnerUserSsn?.trim(),
            address: state.partnerUserAddress?.trim() || undefined
          },
          discountUserInput: {
            ...mainUserRegistration.discountUserInput,
            participantsAmount: 2,
            partnerUser: {
              isStudent: state.isPartnerStudent,
              isSibling: state.isPartnerSibling,
              isSenior: state.isPartnerSenior,
              userActivePeriods: state.partnerUserActivePeriods
            }
          }
        }
      : mainUserRegistration

    // save registered partners from state (simplified) to discountUserInput
    registrationData.discountUserInput.registeredPartners = getRegisteredPartnersFromState(state.registeredPartners)

    const saveRegistration = useSplitRegistration ? saveUserRegistrationV4 : saveUserRegistrationV3
    saveRegistration(registrationData)
      .then((response) => {
        if (response.enrollmentId) {
          onSetEnrollmentId(response.enrollmentId)
          queryClient.removeQueries(obfuscatedEnrollmentQueryKey(response.enrollmentId))
        }
        try {
          const profile = {
            name: state.name,
            email: state.email,
            phone: state.phoneNumber,
            role: state.role ?? mainUserRole ?? null
          }
          localStorage.setItem(StorageKey.RETURNING_USER_PROFILE, JSON.stringify(profile))
        } catch {
          // Ignore storage errors — prefill is a convenience feature
        }
        onSetIsRegistrationSubmitted()
      })
      .catch((error) => {
        console.error(error)
        setSubmitError(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [
    state,
    activity,
    isCheckboxDisabled,
    hasPartner,
    onNextStep,
    onSetIsRegistrationSubmitted,
    onSetEnrollmentId,
    useSplitRegistration
  ])

  const onOpenExternalLink = (e: React.MouseEvent, url: string) => {
    e.stopPropagation()
    window.open(url, '_blank')
  }

  const handleCopySupportEmail = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const canCopy = typeof navigator !== 'undefined' && !!navigator.clipboard?.writeText
    if (!canCopy) return

    e.preventDefault()
    e.stopPropagation()
    navigator.clipboard
      .writeText(SUPPORT_EMAIL)
      .then(() => setEmailCopied(true))
      .catch((err) => {
        console.error('Failed to copy support email:', err)
        window.location.href = `mailto:${SUPPORT_EMAIL}`
      })
  }

  const submitErrorContactSupportParts = getGrooverText('registrationStep3SubmitErrorContactSupport').split('{0}')

  useEffect(() => {
    if (!enrollmentPublic.data) return
    if (enrollmentPublic.status == 'success') {
      const { status } = enrollmentPublic.data as EnrollmentPublic
      if (!!status) {
        setEnrollmentStatus(status)
      }
    }
  }, [state.enrollmentId, enrollmentPublic.data])

  useEffect(() => {
    if (enrollmentStatus === EnrollmentStatus.Confirmed) {
      onNextStep()
    }
  }, [enrollmentStatus])

  const LinkRow = styled(Box)`
    cursor: pointer;
    display: flex;
    align-items: center;

    input[type='checkbox'] {
      cursor: pointer;
    }
  `

  const Link = styled(Box)`
    cursor: pointer;
    color: #0066cc;
    text-decoration: underline;

    &:hover {
      color: #0052a3;
      text-decoration: none;
    }

    &:active {
      color: #003d7a;
    }
  `

  const LinkBox = styled('label')`
    display: flex;
    flex-wrap: wrap;
    cursor: pointer;
    width: 100%;

    > * {
      margin-right: ${FOUR_PX};
    }
  `

  const GreenInfoBox = styled(Box)`
    background: #eaffea;
    border: 1.5px solid #7ed957;
    border-radius: 10px;
    padding: 18px 20px;
    margin: 16px 0;
  `

  const BindingInfoBox = styled(Box)`
    background-color: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 10px;
    padding: 12px 16px;
    margin-top: 8px;
  `

  return (
    <>
      <Box gap={SIXTEEN_PX}>
        <Box direction="row" gap={SIXTEEN_PX}>
          {state.isRegistrationCompleted ||
            (!!state.obfuscatedEnrollment?.mainUser && <img src={iconCompleted} width={24} height={24} />)}
          <span style={{ ...getGrooverTextStyle('subtitle'), color: 'rgba(39, 42, 48, 1)' }}>
            {getGrooverText('registrationHeadersAndStatusHeaderSummaryAndApproval')}
          </span>
        </Box>
      </Box>
      {enrollmentStatus === EnrollmentStatus.Pending ? (
        <CourseRegistrationApprovalWaiting />
      ) : (
        <>
          <span>{getGrooverText('registrationStep4ApprovalInfoApprovalPositive')}</span>
          <GreenInfoBox gap={EIGHT_PX}>
            {!!activity.approvalRequired && activity.approvalRequired && (
              <Box direction="row" align="center" gap={EIGHT_PX}>
                <img src={checkIcon} width={20} height={16} alt="Check icon" />
                <span>{getGrooverText('registrationStep4ApprovalInfoApprovalStep1')}</span>
              </Box>
            )}
            {activity.price !== 0 && (
              <Box direction="row" align="center" gap={EIGHT_PX}>
                <img src={checkIcon} width={20} height={16} alt="Check icon" />
                <span>{getGrooverText('registrationStep3AfterRegistrationChoosePayment')}</span>
              </Box>
            )}
            <Box direction="row" align="center" gap={EIGHT_PX}>
              <img src={checkIcon} width={20} height={16} alt="Check icon" />
              <span>{getGrooverText('registrationStep4ApprovalInfoApprovalStep2')}</span>
            </Box>
            {activity.price !== 0 && (
              <>
                <Box direction="row" align="center" gap={EIGHT_PX}>
                  <img src={checkIcon} width={20} height={16} alt="Check icon" />
                  <span>{getGrooverText('registrationStep4ApprovalInfoApprovalStep3')}</span>
                </Box>
                <Box direction="row" align="center" gap={EIGHT_PX}>
                  <img src={checkIcon} width={20} height={16} alt="Check icon" />
                  <span>{getGrooverText('registrationStep4ApprovalInfoReceiptAvailable')}</span>
                </Box>
              </>
            )}
          </GreenInfoBox>
          {activity.price !== 0 && (
            <BindingInfoBox fullWidth direction="row" align="center" gap={EIGHT_PX}>
              <InfoOutlined sx={{ width: 20, height: 20, color: '#9a3412' }} />
              <span style={{ ...getGrooverTextStyle('body.regular'), color: '#9a3412', fontWeight: 600 }}>
                {getGrooverText('registrationStep3BindingInfo')}
              </span>
            </BindingInfoBox>
          )}
          <LinkRow fullWidth direction="row" align="center" gap={EIGHT_PX}>
            <input
              type="checkbox"
              id="accountApproval"
              checked={isCheckboxChecked || !!state.obfuscatedEnrollment?.mainUser}
              onChange={handleCheckboxChange}
              disabled={state.isRegistrationCompleted || state.isRegistrationSubmitted}
            />
            <LinkBox htmlFor="accountApproval">
              <span>{getGrooverText('registrationStep4ApprovalLegalIApprove')}</span>
              <Link onClick={(e) => onOpenExternalLink(e, privacyPolicyUrl)}>
                <span>{getGrooverText('registrationStep4ApprovalLegalPrivacy')}</span>
              </Link>
              <span>{getGrooverText('registrationStep4ApprovalLegalAnd')}</span>
              <Link onClick={(e) => onOpenExternalLink(e, userConditionsUrl)}>
                <span>{getGrooverText('registrationStep4ApprovalLegalCondition')}</span>
              </Link>
            </LinkBox>
          </LinkRow>
          {roleValidationError && (
            <Box
              fullWidth
              direction="row"
              align="center"
              backgroundColor="#fef2f2"
              gap="15px"
              style={{ padding: '10px' }}
            >
              <ErrorIcon sx={{ color: 'red' }} />
              <span style={{ color: 'red' }}>{getGrooverText('registrationValidationsErrorRoleRequired')}</span>
            </Box>
          )}
          {submitError && (
            <Box
              fullWidth
              direction="row"
              align="center"
              backgroundColor="#fef2f2"
              gap="8px"
              style={{ padding: '10px', flexWrap: 'wrap' }}
            >
              <ErrorIcon sx={{ color: 'red' }} />
              <span style={{ color: 'red' }}>
                {submitErrorContactSupportParts[0]}
                <SupportEmailLink href={`mailto:${SUPPORT_EMAIL}`} onClick={handleCopySupportEmail}>
                  {SUPPORT_EMAIL}
                </SupportEmailLink>
                {submitErrorContactSupportParts[1]}
                {emailCopied && (
                  <span style={{ marginLeft: '8px', fontWeight: 600 }}>
                    {getGrooverText('registrationStep3SubmitErrorEmailCopied')}
                  </span>
                )}
              </span>
            </Box>
          )}
          <Box fullWidth align="flex-end" gap={EIGHT_PX}>
            {isSlowSave && (
              <span style={{ ...getGrooverTextStyle('body.small'), color: 'rgba(39, 42, 48, 0.6)' }}>
                {getGrooverText('registrationStep3SavingSlowNotice')}
              </span>
            )}
            {!state.isRegistrationCompleted ? (
              <ContinueButton
                isDisabled={
                  (!isCheckboxChecked || !hasMainUser || state?.hasFormErrors || isLoading) &&
                  !state.obfuscatedEnrollment?.mainUser
                }
                onClick={!!state.obfuscatedEnrollment?.mainUser ? onNextStep : handleContinue}
                disabled={
                  (!isCheckboxChecked || !hasMainUser || state?.hasFormErrors || isLoading) &&
                  !state.obfuscatedEnrollment?.mainUser
                }
              >
                <GrooverText textKey="registrationCtactaApproveRegister" />
                {isLoading ? <Spinner /> : <img src={iconArrow} width={16} height={16} />}
              </ContinueButton>
            ) : null}
          </Box>
        </>
      )}
    </>
  )
}

export default memo(CourseRegistrationStepThreeOpenedContent)
